import { useCallback, useEffect, useState } from "react";
import {
  fetchBeds,
  fetchDwellings,
  fetchFacilities,
  fetchHousingCandidates,
  fetchPossibleBedOccupants,
  fetchRooms,
} from "../services/httpClient";
import { bedsOfRoom, candidatesByCaseId, propertyCounts, roomsOfProperty } from "../helpers/housingAssignment";

export interface HousingInventory {
  dwellings: any[];
  facilities: any[];
  rooms: any[];
  beds: any[];
  housingCandidates: any[];
  possibleOccupants: any[];
  housingByCaseId: Map<number, any>;
  isLoading: boolean;
  hasLoadedOnce: boolean;
  errMsg: string;
  refresh: () => Promise<void>;
  roomsOf: (propertyId: any) => any[];
  bedsOf: (roomId: any) => any[];
  countsFor: (propertyId: any) => ReturnType<typeof propertyCounts>;
}

interface Options {
  // the assignment screens need candidates and assignable people; a read-only summary does not
  withCandidates?: boolean;
  // false while a parent is still deciding whether this screen applies at all
  enabled?: boolean;
}

// One fetch of the whole housing graph. Every screen that shows dwellings, facilities, rooms or
// beds needs the same six lists and the same three derivations, so they come from here rather
// than each screen assembling its own Promise.all and re-deriving counts inline.
export default function useHousingInventory({ withCandidates = false, enabled = true }: Options = {}): HousingInventory {
  const [dwellings, setDwellings] = useState<any[]>([]);
  const [facilities, setFacilities] = useState<any[]>([]);
  const [rooms, setRooms] = useState<any[]>([]);
  const [beds, setBeds] = useState<any[]>([]);
  const [housingCandidates, setHousingCandidates] = useState<any[]>([]);
  const [possibleOccupants, setPossibleOccupants] = useState<any[]>([]);
  const [housingByCaseId, setHousingByCaseId] = useState<Map<number, any>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const core = [fetchDwellings(), fetchFacilities(), fetchRooms(), fetchBeds()];
      const extra = withCandidates ? [fetchHousingCandidates(), fetchPossibleBedOccupants()] : [];
      const res: any[] = await Promise.all([...core, ...extra]);
      const [dwellingRes, facilityRes, roomRes, bedRes, candidateRes, occupantRes] = res;
      const bedList = bedRes[0].data;
      setDwellings(dwellingRes[0].data);
      setFacilities(facilityRes[0].data);
      setRooms(roomRes[0].data);
      setBeds(bedList);

      if (withCandidates) {
        const candidates = candidateRes[0].data;
        setHousingCandidates(candidates);
        setHousingByCaseId(candidatesByCaseId(candidates));
        // each person carries where they sleep, so a picker can say so without re-deriving it
        setPossibleOccupants(
          occupantRes[0].data.map((f: any) => {
            const match = bedList.find((b: any) => b.occupant_type === f.type && b.occupant_id === f.id);
            return {
              ...f,
              name: f.firstname + " " + f.lastname + (match ? ` - ${match.room_name} (${match.name})` : ""),
              key: f.id + f.type,
              hasBed: !!match,
            };
          }),
        );
      }
      setErrMsg("");
    } catch (err: any) {
      setErrMsg(err?.response?.data?.msg || "Could not load housing");
    } finally {
      setHasLoadedOnce(true);
      setIsLoading(false);
    }
  }, [enabled, withCandidates]);

  useEffect(() => {
    if (enabled) refresh();
  }, [enabled, refresh]);

  return {
    dwellings,
    facilities,
    rooms,
    beds,
    housingCandidates,
    possibleOccupants,
    housingByCaseId,
    isLoading,
    hasLoadedOnce,
    errMsg,
    refresh,
    roomsOf: (propertyId: any) => roomsOfProperty(rooms, propertyId),
    bedsOf: (roomId: any) => bedsOfRoom(beds, roomId),
    countsFor: (propertyId: any) => propertyCounts(rooms, beds, propertyId),
  };
}
